import Input from "../Input";
import { useEffect, useRef, useState, useCallback } from "react";
import ToasterService from "utils/toaster.util";
import { AutoCompleteOwnProps } from ".";
import debounce from "lodash.debounce";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import {
  FaHome,
  FaBuilding,
  FaMapMarkerAlt,
  FaHotel,
  FaPlane,
  FaStore,
  FaRoad,
  FaLandmark,
  FaUtensils,
} from "react-icons/fa";
import { MdClear } from "react-icons/md";
import { useLocation } from "react-router-dom";
import timeZoneService from "services/timezone.service";
import {
  createPlacesService,
  getPlaceDetails,
  getPlacePredictions,
  mapGooglePlaceToFormValue,
  waitForGoogleMaps,
} from "utils/googlePlacesAutocomplete.util";

const typeIconMap: Record<string, any> = {
  home: FaHome,
  premise: FaBuilding,
  point_of_interest: FaLandmark,
  street_address: FaRoad,
  route: FaRoad,
  lodging: FaHotel,
  airport: FaPlane,
  food: FaUtensils,
  store: FaStore,
  establishment: FaBuilding,
  city: FaBuilding,
  locality: FaBuilding,
  default: FaMapMarkerAlt,
};

const getAutocompleteInputSx = (endAdornmentSlots = 1, showClear = false) => {
  const iconRoom = 28 + (showClear ? 36 : 0) + endAdornmentSlots * 36;

  return {
    "& .MuiFilledInput-root": {
      paddingRight: "8px",
    },
    "& .MuiFilledInput-input": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    "& .MuiInputLabel-root": {
      maxWidth: `calc(100% - ${iconRoom}px)`,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      paddingRight: 4,
    },
    "& .MuiInputLabel-shrink": {
      maxWidth: `calc((100% - ${iconRoom}px) / 0.75)`,
    },
    "& .MuiInputAdornment-root": {
      flexShrink: 0,
      marginLeft: 0,
      position: "relative",
      zIndex: 1,
      backgroundColor: "inherit",
    },
  };
};

export type AutoCompletesProps = AutoCompleteOwnProps &
  React.ComponentProps<typeof Input> & {
    pickupLat?: number;
    pickupLng?: number;
  };

export default function AutoCompleteApi({
  val,
  value,
  handleChange,
  pickupLat,
  pickupLng,
  ...rest
}: AutoCompletesProps) {
  const ref = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const router = useLocation();
  const sessionTokenRef =
    useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const autocompleteServiceRef =
    useRef<google.maps.places.AutocompleteService | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(
    null,
  );

  const [inputValue, setInputValue] = useState("");
  const [suggestions, setSuggestions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState("");
  const [mapsReady, setMapsReady] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const isSelecting = useRef(false);
  const biasRef = useRef<{ lat?: number; lng?: number }>({});
  biasRef.current = { lat: pickupLat, lng: pickupLng };

  useEffect(() => {
    let cancelled = false;
    waitForGoogleMaps()
      .then((maps) => {
        if (cancelled) return;
        autocompleteServiceRef.current = new maps.places.AutocompleteService();
        placesServiceRef.current = createPlacesService(maps);
        sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
        setMapsReady(true);
      })
      .catch((error) => {
        if (!cancelled) {
          ToasterService.showError(
            error.message || "Google Maps failed to load",
          );
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (val?.value) {
      setInputValue(val.value);
      isSelecting.current = true;
    }
  }, [val?.value]);

  const fetchLocations = useCallback(
    debounce(async (query: string) => {
      if (!query || !mapsReady || !autocompleteServiceRef.current) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const maps = await waitForGoogleMaps();
        if (!sessionTokenRef.current) {
          sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
        }

        const { lat, lng } = biasRef.current;
        const predictions = await getPlacePredictions(
          autocompleteServiceRef.current,
          query,
          sessionTokenRef.current,
          lat,
          lng,
        );
        setSuggestions(predictions);
      } catch (err: any) {
        ToasterService.showError(
          err?.message || "Failed to fetch Google place suggestions",
        );
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 300),
    [mapsReady],
  );

  useEffect(() => {
    if (isSelecting.current) return;
    if (inputValue) {
      const trimmedInput = inputValue.trim();
      const isOnlySpaceAdded =
        trimmedInput === lastSearchTerm && inputValue.endsWith(" ");

      if (!isOnlySpaceAdded && trimmedInput.length > 2) {
        fetchLocations(inputValue);
        setLastSearchTerm(trimmedInput);
      } else if (trimmedInput.length <= 2) {
        setSuggestions([]);
        setShowSuggestions(false);
      }
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      setLastSearchTerm("");
    }
  }, [inputValue, fetchLocations, lastSearchTerm]);

  useEffect(() => {
    if (showSuggestions && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [showSuggestions, suggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    isSelecting.current = false;
  };

  const handleSuggestionClick = async (
    prediction: google.maps.places.AutocompletePrediction,
  ) => {
    if (!placesServiceRef.current || !sessionTokenRef.current) {
      ToasterService.showError("Google Maps is not ready yet");
      return;
    }

    setLoading(true);
    try {
      const place = await getPlaceDetails(
        placesServiceRef.current,
        prediction.place_id,
        sessionTokenRef.current,
      );
      const mapped = mapGooglePlaceToFormValue(
        place,
        prediction.description,
      );
      if (!mapped) {
        ToasterService.showError(
          `No details available for input: '${prediction.description}'`,
        );
        return;
      }

      isSelecting.current = true;
      const coords = mapped.details.coordinates as {
        lat: number;
        lng: number;
      };
      if (router.pathname.includes("zone")) {
        timeZoneService.getTimeZone(coords.lat, coords.lng);
      }

      setInputValue(mapped.value);
      setShowSuggestions(false);
      handleChange(mapped);

      const maps = await waitForGoogleMaps();
      sessionTokenRef.current = new maps.places.AutocompleteSessionToken();
    } catch (err: any) {
      ToasterService.showError(
        err?.message || "Failed to load selected place details",
      );
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (types?: string[]) => {
    if (!types || types.length === 0) return typeIconMap.default;
    for (const type of types) {
      if (typeIconMap[type]) return typeIconMap[type];
    }
    return typeIconMap.default;
  };

  const hasExtraEndAdornment = Boolean(rest.InputProps?.endAdornment);
  const showClearButton = Boolean(inputValue);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <Input
        inputRef={ref}
        placeholder={`Enter ${rest.label} Location`}
        {...rest}
        value={inputValue}
        label={val?.details?.name || rest.label}
        title={val?.details?.name || rest.label}
        sx={{
          ...(rest.sx as object),
          ...getAutocompleteInputSx(
            hasExtraEndAdornment ? 1 : 0,
            showClearButton,
          ),
        }}
        onChange={handleInputChange}
        onKeyDown={(e) => {
          rest.onKeyDown?.(e);
          if (e.key === "Enter") e.preventDefault();
        }}
        autoComplete="off"
        InputProps={{
          ...rest.InputProps,
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{
                gap: 0,
                flexShrink: 0,
                maxHeight: "100%",
              }}
            >
              {inputValue && (
                <IconButton
                  size="small"
                  aria-label="Clear"
                  sx={{ flexShrink: 0 }}
                  onClick={() => {
                    setInputValue("");
                    setSuggestions([]);
                    setShowSuggestions(false);
                    handleChange({ value: "" });
                    setTimeout(() => ref.current?.focus(), 0);
                  }}
                >
                  <MdClear size={18} color="#888" />
                </IconButton>
              )}
              {rest.InputProps && rest.InputProps.endAdornment}
            </InputAdornment>
          ),
        }}
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul
          style={{
            position: "fixed",
            zIndex: 9999,
            background: "#fff",
            width: `${dropdownPosition.width || containerRef.current?.offsetWidth || 0}px`,
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
            margin: 0,
            padding: 0,
            listStyle: "none",
            maxHeight: 280,
            overflowY: "auto",
            border: "1px solid #e0e0e0",
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            transition: "box-shadow 0.2s",
          }}
        >
          {suggestions.map((prediction, idx) => {
            const Icon = getTypeIcon(prediction.types);
            const mainText =
              prediction.structured_formatting?.main_text ||
              prediction.description;
            const secondaryText =
              prediction.structured_formatting?.secondary_text || "";

            return (
              <li
                key={prediction.place_id || idx}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  padding: "12px 16px",
                  cursor: "pointer",
                  borderBottom:
                    idx !== suggestions.length - 1
                      ? "1px solid #f0f0f0"
                      : undefined,
                  transition: "background 0.15s",
                }}
                onClick={() => handleSuggestionClick(prediction)}
                onMouseOver={(e) =>
                  (e.currentTarget.style.background = "#f5f7fa")
                }
                onMouseOut={(e) => (e.currentTarget.style.background = "#fff")}
              >
                <span
                  style={{
                    fontSize: 18,
                    minWidth: 18,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Icon />
                </span>
                <span
                  style={{ flex: 1, display: "flex", flexDirection: "column" }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      color: "#222",
                      fontSize: 14,
                      overflow: "hidden",
                      textOverflow: "clip",
                      whiteSpace: "nowrap",
                      maxWidth: "334px",
                    }}
                  >
                    {mainText}
                  </span>
                  {secondaryText ? (
                    <span
                      style={{
                        color: "#888",
                        fontSize: 12,
                        fontWeight: 400,
                      }}
                    >
                      {secondaryText}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: `${dropdownPosition.top || (containerRef.current ? containerRef.current.getBoundingClientRect().bottom : 0)}px`,
            left: `${dropdownPosition.left || (containerRef.current ? containerRef.current.getBoundingClientRect().left : 0)}px`,
            background: "#fff",
            width: `${dropdownPosition.width || containerRef.current?.offsetWidth || 0}px`,
            zIndex: 10000,
            padding: "12px 16px",
            border: "1px solid #e0e0e0",
            borderRadius: 12,
            boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
          }}
        >
          Loading...
        </div>
      )}
    </div>
  );
}
